/**
 * Airline telephony (spoken names) and fleets. Fleets are ASSUMPTIONS: a handful of types
 * per airline weighted roughly by what they fly; openScope's airline files (MIT) were the
 * reference. Airports can extend both via `x_atc.telephony` and `x_atc.fleets`.
 */

export const TELEPHONY: Record<string, string> = {
  UAL: 'United', DAL: 'Delta', AAL: 'American', SWA: 'Southwest', ASA: 'Alaska', SKW: 'SkyWest', JBU: 'JetBlue',
  FFT: 'Frontier', NKS: 'Spirit', HAL: 'Hawaiian', QXE: 'Horizon', CPZ: 'Compass', SCX: 'Sun Country', WJA: 'WestJet',
  ACA: 'Air Canada', FDX: 'FedEx', UPS: 'UPS', AAY: 'Allegiant', VRD: 'Redwood', ENY: 'Envoy', RPA: 'Brickyard',
  UAE: 'Emirates', BAW: 'Speedbird', DLH: 'Lufthansa', AFR: 'Air France', KLM: 'KLM', VIR: 'Virgin', SWR: 'Swiss',
  AUA: 'Austrian', IBE: 'Iberia', TAP: 'Air Portugal', THY: 'Turkish', RYR: 'Ryanair', EZY: 'Easy', WZZ: 'Wizz Air',
  LOT: 'LOT', SAS: 'Scandinavian', FIN: 'Finnair', AZA: 'Alitalia', ITY: 'Itarrow', BEL: 'Beeline', AEE: 'Aegean',
  ELY: 'El Al', ISR: 'Israir', AIZ: 'Arkia', ETH: 'Ethiopian', QTR: 'Qatari', ETD: 'Etihad', SVA: 'Saudia',
  AAR: 'Asiana', ANA: 'All Nippon', JAL: 'Japan Air', KAL: 'Korean Air', CCA: 'Air China', CES: 'China Eastern',
  CSN: 'China Southern', CPA: 'Cathay', AIC: 'Air India', SIA: 'Singapore', QFA: 'Qantas', ANZ: 'New Zealand',
  MXA: 'Mexicana', AMX: 'Aeromexico', VOI: 'Volaris', LAN: 'LAN Chile', TAM: 'TAM', GLO: 'Gol',
  N: '', // general aviation: the registration is spoken as-is
};

/** Weighted ICAO types per airline; the first entry is the default fleet. */
export const FLEETS: Record<string, [string, number][]> = {
  UAL: [['B738', 30], ['B739', 20], ['A320', 15], ['B752', 5], ['B772', 4], ['B77W', 3], ['B789', 4], ['A319', 8]],
  DAL: [['B738', 20], ['A321', 20], ['B752', 10], ['A320', 10], ['B739', 10], ['A333', 3], ['B763', 3], ['A359', 2]],
  AAL: [['B738', 40], ['A321', 25], ['A319', 15], ['B772', 3], ['B788', 3]],
  SWA: [['B738', 60], ['B737', 30], ['B38M', 10]],
  ASA: [['B738', 40], ['B739', 30], ['A320', 10], ['B737', 10]],
  SKW: [['E175', 60], ['CRJ7', 20], ['CRJ9', 20]],
  JBU: [['A320', 50], ['A321', 30], ['E190', 20]],
  FFT: [['A320', 60], ['A321', 30], ['A20N', 10]],
  NKS: [['A320', 50], ['A321', 30], ['A319', 20]],
  HAL: [['A321', 40], ['A332', 40], ['B712', 20]],
  QXE: [['DH8D', 50], ['E175', 50]],
  CPZ: [['E175', 100]],
  SCX: [['B738', 100]],
  WJA: [['B738', 70], ['B737', 30]],
  ACA: [['A320', 30], ['A321', 20], ['B738', 20], ['B77W', 5], ['B789', 5], ['A333', 5]],
  FDX: [['B77F', 20], ['MD11', 30], ['B763', 40], ['A306', 10]],
  UPS: [['B763', 40], ['MD11', 30], ['B748', 10], ['A306', 20]],
  UAE: [['B77W', 60], ['A388', 40]],
  BAW: [['B77W', 30], ['A388', 10], ['B789', 20], ['A320', 20], ['A35K', 10]],
  DLH: [['A343', 20], ['A388', 10], ['B748', 20], ['A320', 30], ['A359', 20]],
  AFR: [['B77W', 40], ['A359', 20], ['A320', 30], ['B772', 10]],
  KLM: [['B77W', 30], ['B789', 30], ['B738', 30], ['A333', 10]],
  VIR: [['B789', 40], ['A35K', 40], ['A333', 20]],
  SWR: [['A320', 40], ['A333', 20], ['B77W', 20], ['A343', 20]],
  AUA: [['A320', 50], ['B772', 20], ['E195', 30]],
  IBE: [['A320', 40], ['A321', 20], ['A333', 20], ['A359', 20]],
  TAP: [['A320', 40], ['A321', 20], ['A339', 20], ['A21N', 20]],
  THY: [['A321', 30], ['B738', 30], ['A333', 20], ['B77W', 10], ['A359', 10]],
  RYR: [['B738', 90], ['B38M', 10]],
  EZY: [['A320', 60], ['A319', 20], ['A321', 20]],
  WZZ: [['A321', 60], ['A320', 40]],
  LOT: [['B738', 40], ['E195', 30], ['B788', 30]],
  SAS: [['A320', 50], ['A20N', 30], ['A333', 20]],
  AZA: [['A320', 60], ['A332', 20], ['B772', 20]],
  ITY: [['A320', 60], ['A21N', 20], ['A339', 20]],
  AEE: [['A320', 80], ['A321', 20]],
  ELY: [['B738', 40], ['B789', 30], ['B77W', 15], ['B739', 15]],
  ISR: [['A320', 60], ['A21N', 40]],
  AIZ: [['A321', 50], ['E195', 50]],
  ETH: [['B788', 40], ['A359', 40], ['B77W', 20]],
  QTR: [['B77W', 40], ['A359', 30], ['A388', 10], ['B788', 20]],
  ETD: [['B789', 50], ['A388', 20], ['A35K', 30]],
  AAR: [['A333', 30], ['A359', 30], ['B77W', 20], ['A388', 20]],
  ANA: [['B789', 40], ['B77W', 40], ['B788', 20]],
  JAL: [['B789', 50], ['B77W', 30], ['B788', 20]],
  KAL: [['B77W', 40], ['A388', 20], ['B789', 20], ['A333', 20]],
  CCA: [['B77W', 50], ['A333', 30], ['B789', 20]],
  CES: [['B77W', 50], ['A333', 30], ['A359', 20]],
  CSN: [['B77W', 40], ['A388', 20], ['A333', 20], ['B789', 20]],
  CPA: [['B77W', 50], ['A359', 40], ['A35K', 10]],
  AIC: [['B77W', 60], ['B788', 40]],
  SIA: [['A359', 50], ['B77W', 30], ['A388', 20]],
  QFA: [['B789', 40], ['A388', 30], ['A333', 30]],
  ANZ: [['B789', 60], ['B77W', 40]],
  AMX: [['B738', 50], ['B789', 30], ['B788', 20]],
  VOI: [['A320', 60], ['A21N', 40]],
  N: [['C172', 30], ['SR22', 25], ['PA28', 20], ['BE58', 10], ['C182', 15]],
};
export const FAST_GA_FLEET: [string, number][] = [['C56X', 40], ['C750', 30], ['LJ35', 30]];
export const DEFAULT_FLEET: [string, number][] = [['B738', 50], ['A320', 50]];

export function telephony(code: string, extra?: Record<string, string>): string {
  return extra?.[code] ?? TELEPHONY[code] ?? code;
}

export function fleetFor(code: string, fleet: string | undefined, extra?: Record<string, [string, number][]>): [string, number][] {
  if (code === 'N' && fleet === 'fastGA') return FAST_GA_FLEET;
  return extra?.[code] ?? FLEETS[code] ?? DEFAULT_FLEET;
}

const DIGIT_WORDS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'niner'];
const TEENS = ['ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
const TENS = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

/**
 * How a crew says its flight number. ICAO Annex 10 speaks every digit ("Israir three zero three");
 * FAA practice (AIM 4-2-4) groups airline flight numbers ("United twelve thirty-four"); and many
 * crews everywhere say a zero as "oh" ("Israir three oh three"). The telephony designator always
 * leads: the three-letter code is never said as a word or spelt, and a Type C callsign has no
 * abbreviated form (docs/phraseology-research.md).
 */
export type CallsignForm = 'digits' | 'oh' | 'group';
/** Radio conventions an airport's controllers and crews follow. */
export type RadioStyle = 'icao' | 'faa';

/** The US and its Pacific territories use FAA phraseology; everywhere else here follows ICAO. */
export function radioStyleFor(icao: string, override?: RadioStyle): RadioStyle {
  return override ?? (/^[KP]/.test(icao.toUpperCase()) ? 'faa' : 'icao');
}

/**
 * The form one aircraft uses for the whole session, decided from its callsign so no random stream
 * moves. ASSUMPTION: shares tuned by ear; FAA airline crews mostly group, ICAO crews mostly spell
 * digits with an occasional "oh". `chance` is the realism's phrase variation (0 keeps digits).
 */
export function callsignFormFor(callsign: string, style: RadioStyle, chance: number, roll: number): CallsignForm {
  if (chance <= 0) return 'digits';
  if (style === 'faa') return roll < Math.min(0.85, chance * 1.4) ? 'group' : 'digits';
  return roll < chance * 0.4 ? 'oh' : 'digits';
}

/** "1234" → "twelve thirty-four", "351" → "three fifty-one", "305" → "three oh five"; null when it does not group. */
export function groupedNumber(num: string): string | null {
  if (!/^[1-9]\d{0,3}$/.test(num)) return null;
  const pair = (p: string): string => {
    const n = Number(p);
    if (p.length === 1) return DIGIT_WORDS[n]!;
    if (p[0] === '0') return `oh ${DIGIT_WORDS[Number(p[1])]}`;
    if (n < 20) return TEENS[n - 10]!;
    return n % 10 ? `${TENS[Math.floor(n / 10)]}-${DIGIT_WORDS[n % 10] === 'niner' ? 'nine' : DIGIT_WORDS[n % 10]}` : TENS[n / 10]!;
  };
  if (num.length <= 2) return pair(num);
  const head = num.slice(0, num.length - 2), tail = num.slice(-2);
  if (tail === '00') return num.length === 4 && num[1] === '0' ? `${DIGIT_WORDS[Number(num[0])]} thousand` : `${pair(head)} hundred`;
  return `${pair(head)} ${pair(tail)}`;
}

/** "UAL123" → "United one two three"; "N4521B" → "November four five two one bravo". */
export function spokenCallsign(callsign: string, airline: string, extra?: Record<string, string>, form: CallsignForm = 'digits'): string {
  const number = callsign.slice(airline.length);
  const name = telephony(airline, extra);
  const grouped = name && form === 'group' ? groupedNumber(number) : null;
  const spelt = grouped ?? number.split('').map((c) => (/\d/.test(c) ? (c === '0' && form === 'oh' && name ? 'oh' : DIGIT_WORDS[Number(c)]) : NATO[c.toUpperCase()] ?? c)).join(' ');
  return name ? `${name} ${spelt}` : `November ${spelt}`;
}

/**
 * Accent keys for synthesized radio speech. The words for each live on the speech server
 * (voice/speech.mjs ACCENTS), so a page can only pick a key, never write the prompt.
 */
export const ACCENT_KEYS = ['international', 'american', 'british', 'israeli', 'german', 'french', 'italian', 'spanish', 'portuguese', 'dutch', 'scandinavian', 'polish', 'hungarian', 'turkish', 'greek', 'arabic', 'ethiopian', 'indian', 'chinese', 'japanese', 'korean', 'latin-american', 'australian'] as const;
export type Accent = (typeof ACCENT_KEYS)[number];

/** Where an airline's crews mostly come from, as an accent. ASSUMPTION: one accent per airline is a fair simplification. */
export const AIRLINE_ACCENT: Record<string, Accent> = {
  UAL: 'american', DAL: 'american', AAL: 'american', SWA: 'american', ASA: 'american', SKW: 'american', JBU: 'american',
  FFT: 'american', NKS: 'american', HAL: 'american', QXE: 'american', CPZ: 'american', SCX: 'american', WJA: 'american',
  ACA: 'american', FDX: 'american', UPS: 'american', AAY: 'american', VRD: 'american', ENY: 'american', RPA: 'american', EJA: 'american',
  BAW: 'british', EZY: 'british', VIR: 'british', RYR: 'british', DLH: 'german', SWR: 'german', AUA: 'german',
  AFR: 'french', KLM: 'dutch', IBE: 'spanish', TAP: 'portuguese', THY: 'turkish', WZZ: 'hungarian', LOT: 'polish',
  SAS: 'scandinavian', FIN: 'scandinavian', AZA: 'italian', ITY: 'italian', AEE: 'greek',
  ELY: 'israeli', ISR: 'israeli', AIZ: 'israeli', ETH: 'ethiopian', QTR: 'arabic', ETD: 'arabic', UAE: 'arabic', SVA: 'arabic',
  AAR: 'korean', KAL: 'korean', ANA: 'japanese', JAL: 'japanese', CCA: 'chinese', CES: 'chinese', CSN: 'chinese', CPA: 'chinese',
  AIC: 'indian', SIA: 'international', QFA: 'australian', ANZ: 'australian',
  MXA: 'latin-american', AMX: 'latin-american', VOI: 'latin-american', LAN: 'latin-american', TAM: 'latin-american', GLO: 'latin-american',
};

/** ICAO nationality prefixes to the accent a local controller (and local GA pilot) speaks with. */
const PREFIX_ACCENT: [RegExp, Accent][] = [
  [/^[KP]/, 'american'], [/^C/, 'american'], [/^EG/, 'british'], [/^LL/, 'israeli'], [/^ED/, 'german'], [/^LS|^LO/, 'german'],
  [/^LF/, 'french'], [/^LI/, 'italian'], [/^LE/, 'spanish'], [/^LP/, 'portuguese'], [/^EH/, 'dutch'], [/^E[KNS]/, 'scandinavian'],
  [/^EP/, 'polish'], [/^LH/, 'hungarian'], [/^LT/, 'turkish'], [/^LG/, 'greek'], [/^O/, 'arabic'], [/^HA/, 'ethiopian'],
  [/^V[AEIO]/, 'indian'], [/^Z/, 'chinese'], [/^RJ/, 'japanese'], [/^RK/, 'korean'], [/^Y/, 'australian'], [/^M|^S/, 'latin-american'],
];

const isAccent = (v: string | undefined): v is Accent => !!v && (ACCENT_KEYS as readonly string[]).includes(v);

/** The accent of the airport's own people: its controllers and local general aviation. */
export function airportAccent(icao: string, override?: string): Accent {
  if (isAccent(override)) return override;
  return PREFIX_ACCENT.find(([re]) => re.test(icao.toUpperCase()))?.[1] ?? 'international';
}

/** A crew's accent: the airport's table, then the built-in one; GA and unknown operators sound local. */
export function accentFor(airline: string, airport: { icao: string; accent?: string; accents?: Record<string, string> }): Accent {
  const own = airport.accents?.[airline] ?? AIRLINE_ACCENT[airline];
  return isAccent(own) ? own : airportAccent(airport.icao, airport.accent);
}

export function spokenDigits(n: number | string): string {
  return String(n).split('').map((c) => (/\d/.test(c) ? DIGIT_WORDS[Number(c)] : c)).join(' ');
}

export const NATO: Record<string, string> = {
  A: 'alpha', B: 'bravo', C: 'charlie', D: 'delta', E: 'echo', F: 'foxtrot', G: 'golf', H: 'hotel', I: 'india', J: 'juliett', K: 'kilo', L: 'lima', M: 'mike',
  N: 'november', O: 'oscar', P: 'papa', Q: 'quebec', R: 'romeo', S: 'sierra', T: 'tango', U: 'uniform', V: 'victor', W: 'whiskey', X: 'x-ray', Y: 'yankee', Z: 'zulu',
};
